import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Loader2, ShieldAlert, X, CloudUpload, CheckCircle2 } from "lucide-react";
import { API } from "../../config";

export const TestDataResetCard = ({ isSuperAdmin }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-8 bg-[#111] border border-lojack-red/30 rounded-2xl p-5" data-testid="test-data-reset-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-white text-sm font-semibold flex items-center gap-2">
            <ShieldAlert size={16} className="text-lojack-red" />
            Zona pericolosa — Reset Dati di Test
          </h3>
          <p className="text-gray-400 text-xs mt-1 leading-relaxed max-w-2xl">
            Elimina TUTTI i dealer, i clienti finali e i dati collegati (contratti, OTP, log email/SMS,
            log Salesforce, wallet). Non tocca account admin, configurazioni, documenti legali e template.
            Prima della cancellazione viene eseguito un backup su TelcaVoIP Cloud (pCloud).
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          disabled={!isSuperAdmin}
          title={!isSuperAdmin ? "Richiede ruolo super admin" : undefined}
          className="text-xs px-4 py-2 rounded-lg border border-lojack-red/40 bg-lojack-red/10 text-lojack-red hover:bg-lojack-red/20 transition flex items-center gap-2 disabled:opacity-40"
          data-testid="test-data-reset-open"
        >
          <Trash2 size={13} />
          Reset dati di test...
        </button>
      </div>
      {open && <ResetModal onClose={() => setOpen(false)} />}
    </div>
  );
};

const ResetModal = ({ onClose }) => {
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [runBackup, setRunBackup] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/test-data-reset/preview`)
      .then((r) => setPreview(r.data))
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Errore caricamento anteprima");
        onClose();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const execute = async () => {
    setExecuting(true);
    try {
      const r = await axios.post(`${API}/admin/test-data-reset/execute`, {
        confirm: confirmText,
        run_backup: runBackup,
      });
      setResult(r.data);
      toast.success(r.data.message);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Reset fallito");
    } finally {
      setExecuting(false);
    }
  };

  const nonEmpty = (preview?.items || []).filter((i) => i.count > 0);
  const canExecute = confirmText.trim().toUpperCase() === (preview?.confirm_phrase || "RESET") && !executing;

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
      style={{ width: "100vw", height: "100vh", top: 0, left: 0 }}
      onClick={onClose}
    >
      <div
        className="bg-[#1A1A1A] rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-lojack-red/40 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="test-data-reset-modal"
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <ShieldAlert size={20} className="text-lojack-red" />
            Reset Dati di Test
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg transition-colors" data-testid="test-data-reset-close">
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {!preview && (
            <div className="flex justify-center py-10">
              <Loader2 size={22} className="animate-spin text-gray-600" />
            </div>
          )}

          {preview && !result && (
            <>
              <p className="text-gray-300 text-sm">
                Verranno eliminati <span className="text-lojack-red font-bold">{preview.total_documents}</span>{" "}
                documenti da {nonEmpty.length} collezioni:
              </p>
              <div className="max-h-56 overflow-y-auto border border-gray-800 rounded-xl" data-testid="test-data-reset-breakdown">
                {nonEmpty.map((i) => (
                  <div key={i.collection} className="flex justify-between px-4 py-2 border-b border-gray-800/60 last:border-0 text-xs">
                    <span className="text-gray-300">{i.label}</span>
                    <span className="text-white font-mono">{i.count}</span>
                  </div>
                ))}
                {nonEmpty.length === 0 && (
                  <p className="text-gray-500 text-sm p-4 text-center">Nessun dato di test presente — già tutto pulito.</p>
                )}
              </div>

              <label className="flex items-center gap-3 bg-gray-800/40 rounded-xl p-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={runBackup}
                  onChange={(e) => setRunBackup(e.target.checked)}
                  className="accent-lojack-red"
                  data-testid="test-data-reset-backup-toggle"
                />
                <span className="text-sm text-gray-300 flex items-center gap-2">
                  <CloudUpload size={14} className="text-sky-400" />
                  Esegui backup su TelcaVoIP Cloud prima della cancellazione (consigliato)
                  {!preview.pcloud_configured && (
                    <span className="text-yellow-400 text-xs">(pCloud non configurato in questo ambiente)</span>
                  )}
                </span>
              </label>

              <div>
                <label className="text-gray-400 text-xs">
                  Per confermare digita <span className="text-lojack-red font-mono font-bold">{preview.confirm_phrase}</span>
                </label>
                <input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={preview.confirm_phrase}
                  className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none text-sm font-mono"
                  data-testid="test-data-reset-confirm-input"
                />
              </div>
            </>
          )}

          {result && (
            <div className="space-y-3" data-testid="test-data-reset-result">
              <div className="flex items-center gap-2 text-emerald-400 font-semibold">
                <CheckCircle2 size={18} /> {result.message}
              </div>
              {result.backup && !result.backup.skipped && (
                <p className="text-xs text-gray-400">
                  Backup pCloud: {result.backup.ok ? `OK — ${result.backup.filename}` : `fallito (${result.backup.error || "errore"})`}
                </p>
              )}
              <div className="max-h-56 overflow-y-auto border border-gray-800 rounded-xl">
                {Object.entries(result.deleted)
                  .filter(([, v]) => v > 0)
                  .map(([k, v]) => (
                    <div key={k} className="flex justify-between px-4 py-2 border-b border-gray-800/60 last:border-0 text-xs">
                      <span className="text-gray-300 font-mono">{k}</span>
                      <span className="text-white font-mono">{v}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-gray-700">
          {!result ? (
            <>
              <button onClick={onClose} className="px-5 py-2.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors">
                Annulla
              </button>
              <button
                onClick={execute}
                disabled={!canExecute || nonEmpty.length === 0}
                className="px-5 py-2.5 rounded-lg bg-lojack-red text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-red-700 transition-colors"
                data-testid="test-data-reset-execute"
              >
                {executing ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                {executing ? "Reset in corso..." : `Elimina definitivamente (${preview?.total_documents ?? 0})`}
              </button>
            </>
          ) : (
            <button onClick={() => window.location.reload()} className="btn-lojack px-5 py-2.5 text-sm" data-testid="test-data-reset-done">
              Fatto
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
